import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { Socket } from 'socket.io-client';

import { useLazyGetAppointmentsQuery } from 'services/appointments/appointments';

import { AppointmentItem } from 'store/appointments/appointments.types';
import { setUser } from 'store/user/userSlice';

import { notifyError } from 'shared/Toast/Toast';

import { useAppDispatch } from 'hooks';
import { AppointmentListTypes } from 'utils/enums';
import { parseUpcomingAppointment } from 'utils/appointment';
import { getCancellationToastMessage } from 'utils/cancelation';

import { CommonSocketContextParams, PatientEmailUpdateProps } from './commonSocketContext.types';

export const CommonSocketContext = createContext<CommonSocketContextParams>({
  appointmentUpdatedViaSocket: null
});

export const CommonSocketProvider: React.FC<PropsWithChildren<{ socket: Socket }>> = ({
  children,
  socket
}) => {
  const [appointment, setAppointment] = useState<AppointmentItem | null>(null);
  const [getAppointments] = useLazyGetAppointmentsQuery();
  const dispatch = useAppDispatch();

  const handleSetAppointmentUpdate = useCallback(
    (updatedAppointment: AppointmentItem) => {
      setAppointment(parseUpcomingAppointment(updatedAppointment));
      getAppointments({ appointmentListType: AppointmentListTypes.Upcoming });
      if (updatedAppointment.appointmentStatus === 'cancelled') {
        notifyError(getCancellationToastMessage(updatedAppointment.cancelationInfo), true);
      }
    },
    [getAppointments]
  );

  const handlePatientEmailUpdate = useCallback(
    (params: PatientEmailUpdateProps) => {
      dispatch(setUser({ ...params }));
    },
    [dispatch]
  );

  useEffect(() => {
    socket.on('appointmentUpdate', handleSetAppointmentUpdate);
    socket.on('patientEmailUpdate', handlePatientEmailUpdate);

    return () => {
      socket.off('appointmentUpdate', handleSetAppointmentUpdate);
      socket.off('patientEmailUpdate', handlePatientEmailUpdate);
    };
  }, [handlePatientEmailUpdate, handleSetAppointmentUpdate, socket]);

  const value = useMemo(() => ({ appointmentUpdatedViaSocket: appointment }), [appointment]);

  return <CommonSocketContext.Provider value={value}>{children}</CommonSocketContext.Provider>;
};
